import { env } from 'config/environment'
import { parseFinite } from 'util/number'

export const ASPECT_RATIO = 9 / 16

export function getFitSrc(src: string, width: number, height: number, contain: boolean): string {
  try {
    const url = new URL(src)
    const search = new URLSearchParams(url.search)
    const w = parseFinite(search.get('width'))
    const h = parseFinite(search.get('height'))
    const a = w && h ? w / h : ASPECT_RATIO
    const aspect = width / height

    if (contain) {
      if (a > aspect) height = width / a
      else width = height * a
    } else {
      if (a > aspect) width = height * a
      else height = width / a
    }

    search.set('width', Math.ceil(width).toFixed())
    search.set('height', Math.ceil(height).toFixed())
    url.search = search.toString()
    return url.toString()
  } catch {
    return src
  }
}

/**
 * Получает размеры изображения по URL
 * @param imageUrl URL изображения
 * @returns Promise с размерами {width, height} или null
 */
export async function getImageDimensions(imageUrl: string): Promise<{ width: number, height: number } | null> {
  try {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        resolve({ width: img.width, height: img.height })
      }
      img.onerror = () => {
        reject(new Error('Failed to load image'))
      }
      img.src = imageUrl
    })
  } catch (error) {
    console.warn('Failed to get image dimensions:', error)
    return null
  }
}

/**
 * Получает MIME тип изображения по расширению файла
 * @param imageUrl URL изображения
 * @returns MIME тип изображения
 */
export function getImageMimeType(imageUrl: string): string {
  const extension = imageUrl.split('.').pop()?.toLowerCase()
  switch (extension) {
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg'
    case 'png':
      return 'image/png'
    case 'gif':
      return 'image/gif'
    case 'webp':
      return 'image/webp'
    case 'svg':
      return 'image/svg+xml'
    default:
      return 'image/png' // fallback
  }
}

export function fixImageUrl(src: string): string {
  try {
    const url = new URL(src)
    if (url.host === 'dresscode-stage.s3.eu-central-1.amazonaws.com') {
      url.host = 'imgs-stage.dresscode.ai'
    }
    if (url.host === 'dresscode-stage-2.s3.eu-central-1.amazonaws.com') {
      url.host = 'imgs-stage.dresscode.ai'
    }
    if (url.host === 'dresscode-prod.s3.eu-central-1.amazonaws.com') {
      url.host = 'imgs.dresscode.ai'
    }
    if (url.host === 'dresscode-prod-2.s3.eu-central-1.amazonaws.com') {
      url.host = 'imgs.dresscode.ai'
    }
    if (url.host === 'mosaica-upload.s3.eu-central-1.amazonaws.com') {
      url.host = 'imgs-ai.dresscode.ai'
    }
    if (url.host === 'mosaica-upload-2.s3.eu-central-1.amazonaws.com') {
      url.host = 'imgs-ai.dresscode.ai'
    }
    if (url.host === 'imgs.atelierdigital.ai' || url.host === 'imgs.apiresq.com') {
      url.host = 'imgs.dresscode.ai'
    }
    const atelier = ['.atelierdigital.ai', '.apiresq.com'].find(d => env.frontendUrl.endsWith(d))
    if (atelier && url.host === 'imgs.dresscode.ai') {
      url.host = 'imgs' + atelier
    }
    const showoff = env.frontendUrl.endsWith('.showoff.app')
    if (showoff && url.host === 'imgs.dresscode.ai') {
      url.host = 'imgs.showoff.app'
    }
    url.searchParams.delete('width')
    url.searchParams.delete('height')
    return url.toString()
  } catch {
    return src
  }
}

export function proxyImageUrl(src: string): string {
  src = fixImageUrl(src)
  const url = new URL(env.frontendUrl)
  url.pathname = '/api/image'
  url.searchParams.set('url', src)
  return url.toString()
}
